/**
 * SOLID Review
 * S — Strong. One concern: the store's data, shaped — and normalised — as the table snapshot the
 *     decisions read.
 * O — Good. A new table field is one line here and one in the snapshot schema.
 * L — N/A — functions.
 * I — Strong. One export, the store's type in, the snapshot's out.
 * D — Strong. Types only: the store's (declared here, in application/state) and the Domain's schema.
 */
import type { TGameStoreData } from './gameStoreTypes';
import type { IStateClientTable, IEntityClientRoom, IEntityClientSeat } from '../../domain/ports/schemas/ClientTable';
import { PlayerStatus, MonetizationTrack } from '../../domain/uml-components/primitives';
import type { IEntityRoom, IEntityPlayer } from '../../domain/uml-components/entities';

/**
 * THE STORE AS THE TABLE DECISIONS SEE IT (frontend strict-ports plan, FE1, 2026-10-03). Two readers,
 * which is why it lives in the Application rather than in either of them:
 *
 *   - Infrastructure's `ClientState.snapshot` — what every decision is handed;
 *   - the store's interim middleware, which derives the screen's values after a write that did not
 *     come through a decision (the FE2–FE5 flows, until FE6).
 *
 * NORMALISED, NOT TRUSTED (FE1e). The store is TYPED, but until FE6 not every writer is: the FE2–FE5
 * flows still copy unparsed reads into it. Found by the recovery suite: a restore whose read lacked
 * `pendingClaim.wasBlind` stored `claimWasBlind: undefined`. One such value fails the snapshot's
 * contract, and with it EVERY table decision, silently — the table stops. So each value is read as its
 * type: a boolean is `=== true`, a number must be finite, a string must be a string, and anything else
 * reads as the field's empty value. The fault then degrades to that one field, not to the whole table.
 * An enum has no empty value: an unknown seat status or track reads as what the roster gives a seat it
 * knows nothing about (`buildRosterFromRaw`: ACTIVE, FREE) — no decision reads either, they are carried.
 * A seat without an id, or a room without one, is not a seat or a room, and is left out.
 *
 * Every value is copied, never shared: a snapshot is JSON on its way through the contract, and a caller
 * that edited one must not edit the store.
 */
export function projectClientTable(store: TGameStoreData): IStateClientTable {
  const table: IStateClientTable = {
    // '' is "no identity" spelled wrong; the snapshot's schema refuses it, so it reads as none.
    localPlayerId: text(store.localUser?.playerId) || null,
    room: store.room === null || store.room === undefined || !text(store.room.roomId) ? null : roomOf(store.room),
    phase: store.phase,
    activeRoundId: textOrNull(store.activeRoundId),
    activeRollerId: textOrNull(store.activeRollerId),
    activeChallengerId: textOrNull(store.activeChallengerId),
    lastDiceRoll: store.lastDiceRoll === null || store.lastDiceRoll === undefined ? null : {
      die1: int(store.lastDiceRoll.die1),
      die2: int(store.lastDiceRoll.die2),
      score: int(store.lastDiceRoll.score),
      isMeyer: store.lastDiceRoll.isMeyer === true,
      isDouble: store.lastDiceRoll.isDouble === true,
    },
    minimumClaimScore: int(store.minimumClaimScore),
    lastClaimedScore: intOrNull(store.lastClaimedScore),
    claimWasBlind: store.claimWasBlind === true,
    diceRevealed: store.diceRevealed === true,
    opponentClaim: store.opponentClaim === null || store.opponentClaim === undefined ? null : {
      score: int(store.opponentClaim.score),
      label: text(store.opponentClaim.label),
    },
    revealResult: store.revealResult === null || store.revealResult === undefined ? null : {
      losingPlayerId: text(store.revealResult.losingPlayerId),
      wasBluff: store.revealResult.wasBluff === true,
      claimedScore: int(store.revealResult.claimedScore),
      trueScore: int(store.revealResult.trueScore),
      die1: int(store.revealResult.die1),
      die2: int(store.revealResult.die2),
      newRollerId: text(store.revealResult.newRollerId),
    },
    decisionCountdown: store.decisionCountdown === null || store.decisionCountdown === undefined ? null : {
      roundId: text(store.decisionCountdown.roundId),
      challengerId: text(store.decisionCountdown.challengerId),
      secondsRemaining: int(store.decisionCountdown.secondsRemaining),
    },
    playerLives: Object.fromEntries(Object.entries(store.playerLives ?? {}).map(([id, lives]) => [id, int(lives)])),
    suspendedPlayerIds: (store.suspendedPlayerIds ?? []).filter((id): id is string => typeof id === 'string'),
    roomEnded: store.roomEnded === true,
    tableVersion: store.tableVersion === null || store.tableVersion === undefined || !text(store.tableVersion.roomId) ? null : {
      roomId: store.tableVersion.roomId,
      version: int(store.tableVersion.version),
    },
    connectionState: store.connectionState ?? 'disconnected',
    connectionWasDown: store.connectionWasDown === true,
    lastGameActivityAt: int(store.lastGameActivityAt),
  };
  return table;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function int(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : 0;
}

function intOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : null;
}

const STATUSES: readonly string[] = Object.freeze(Object.values(PlayerStatus));
const TRACKS: readonly string[] = Object.freeze(Object.values(MonetizationTrack));

function oneOf<T extends string>(members: readonly string[], value: unknown, fallback: T): T {
  return typeof value === 'string' && members.includes(value) ? (value as T) : fallback;
}

function roomOf(room: IEntityRoom): IEntityClientRoom {
  return {
    roomId: room.roomId,
    roomCode: text(room.roomCode),
    hostPlayerId: text(room.hostPlayerId),
    players: (room.players ?? []).filter((p) => text(p?.id) !== '').map(seatOf),
    maxPlayers: int(room.maxPlayers),
    minPlayers: int(room.minPlayers),
    isGameActive: room.isGameActive === true,
  };
}

function seatOf(p: IEntityPlayer): IEntityClientSeat {
  return {
    id: p.id,
    displayName: text(p.displayName),
    avatarUrl: textOrNull(p.avatarUrl),
    status: oneOf(STATUSES, p.status, PlayerStatus.ACTIVE),
    lives: { current: int(p.lives?.current), max: int(p.lives?.max), isEliminated: p.lives?.isEliminated === true },
    monetization: {
      track: oneOf(TRACKS, p.monetization?.track, MonetizationTrack.FREE),
      matchCredits: int(p.monetization?.matchCredits),
      hasPremiumAccess: p.monetization?.hasPremiumAccess === true,
    },
    isLocalPlayer: p.isLocalPlayer === true,
    isCurrentTurn: p.isCurrentTurn === true,
    seatIndex: int(p.seatIndex),
  };
}
